// EXTRENAL
import { useApolloClient } from "@apollo/client";
// CONTEXT
import { useNotificationDispatch } from "../../../../contexts/notification.context";
import { useUserState } from "../../../../contexts/user.context";
// LIBS
import { logger } from "../../../../libs/logger";
// FUNCTIONS
import { getCampaignData } from "./getCampaignData";
import { getBannerData } from "./getBannerData";
// TYPES
import { AdformReportingModalSetters, ReportType } from "../types";
import { GET_EXISTING_CREATIVE, LIST_CREATIVES } from "../../../../graphql/queries";

/**
 *
 * Click handler function for opening Adform reporting modal
 * Function is used as a click handler in both campaign and banner reporting specific buttons
 * This determines what type of data needs to be preset on the modal before it renders on user screen
 */
export function openReportingModal({ advertiserId, advertiserName, setAdformReportingModalPresetData, setAdformReportingModalOpen, setAdformReportingCreativeData }: AdformReportingModalSetters): ReportType {
  const user = useUserState();
  const notificationDispatch = useNotificationDispatch();
  const apolloClient = useApolloClient();

  /**
   * These query the client directly rather than via useLazyQuery: on @apollo/client 3.3 the lazy
   * execute function returns void, so awaiting it resolves to undefined instead of a result.
   */
  const getOneCreativeData = async ({ variables }: { variables: { id: string; teamId: string } }) => {
    try {
      return await apolloClient.query({ query: GET_EXISTING_CREATIVE, variables, fetchPolicy: "network-only" });
    } catch (error) {
      logger({ error, user, context: { origin: "openReportingModal_GET_EXISTING_CREATIVE" } });
      throw error;
    }
  };

  const getAllCreativesData = async ({ variables }: { variables: { teamId: string; advertiserId: string } }) => {
    try {
      return await apolloClient.query({ query: LIST_CREATIVES, variables, fetchPolicy: "network-only" });
    } catch (error) {
      logger({ error, user, context: { origin: "openReportingModal_LIST_CREATIVES" } });
      throw error;
    }
  };

  return async (scope: string, creativeId: string) => {
    try {
      /* Get campaign or banner data before opening the modal.
       * This is so that the modal is pre-filled with this information, and doesn't open if there is no banner/campaign.
       */
      if (scope === "campaign") {
        await getCampaignData({ getAllCreativesData, user, advertiserId, advertiserName, setAdformReportingModalPresetData, setAdformReportingModalOpen, notificationDispatch });
      }
      if (scope === "banner") {
        await getBannerData({ getOneCreativeData, creativeId, user, setAdformReportingCreativeData, setAdformReportingModalPresetData, setAdformReportingModalOpen });
      }
    } catch (error) {
      logger({ error, user, context: { origin: "openReportingModal_openReportingModal" } });
      return notificationDispatch({
        type: "ADD",
        payload: {
          header: "Error",
          message: error.message || "Something went wrong",
          type: "error",
          autoDismiss: false,
        },
      });
    }
  };
}
